import { collection as libxCollection, ICollection, IModelOptions } from 'libx'
import { Model } from './model'
import { action, observe, IArrayUpdate, IArraySplice } from 'mobx'
import { Observable, from } from 'rxjs'
import { concatMap, share } from 'rxjs/operators'

/**
 * Model options.
 */
export const ModelOpts = {
  none: (): IModelOptions => ({}),
}

/**
 * Collection options.
 */
export interface CollectionOptions<A, T extends Model<A, unknown>> {
  /**
   * May be used by callbacks like `getModelId` and `getDataId`
   */
  idAttribute?: keyof T | keyof A
  /**
   * Called when the collection wants to add a new item.
   */
  create: (attributes: Partial<A>) => T
  /**
   * Called when the collection wants to update an existing item with more data.
   */
  update?: (existing: T, input: Partial<A>) => T
  /**
   * Used to get the ID of a model that was passed in. Whatever is returned from
   * this should be coercible to a string, and is used for indexing.
   */
  getModelId?: (obj: T, opts: CollectionOptions<A, T>) => any
  /**
   * Used to get an ID from input data, used to determine whether to create
   * or update.
   */
  getDataId?: (attrs: Partial<A>, opts: CollectionOptions<A, T>) => any
}

/**
 * If `T` is an array of `I`, the resulting type is `I`, otherwise it is `T`.
 */
type Dearrayify<T> = T extends Array<infer I> ? I : T

/**
 * Model collection.
 */
export type Collection<A, T extends Model<A, unknown>> = Omit<
  ICollection<T>,
  'create' | 'update' | 'set' | 'reset'
> & {
  /**
   * Multi-version of `create`.
   */
  create(data: Partial<A>[]): T[]
  /**
   * Like `set`, but will add regardless of whether an id is present or not.
   * This has the added risk of resulting multiple model instances if you don't make sure
   * to update the existing model once you do have an id. The model id is what makes the whole
   * one-instance-per-entity work.
   */
  create(data: Partial<A>): T
  /**
   * Same as the singular version, but with multiple.
   */
  set<
    D extends Array<Partial<A> | null | undefined> | undefined,
    R = T | Exclude<Dearrayify<D>, Partial<A>>,
  >(
    data: D,
  ): D extends undefined ? Array<R> | undefined : Array<R>
  /**
   * Same as the singular version, but with multiple.
   */
  set(data: Partial<A>[]): T[]
  /**
   * Adds a single item and maps it using the mapper in the options.
   */
  set(
    data: Partial<A> | null | undefined,
  ): A extends undefined ? undefined : A extends null ? null : T
  /**
   * Adds elements that were not in the collection, updates elements that are, and removes elements that are not
   * represented in `data` (using `getDataId`).
   *
   * @param data the data to reset to. If undefined, does nothing.
   */
  reset(data?: Partial<A>[]): Collection<A, T>
  /**
   * An observable that emits removed values.
   */
  removals$: Observable<T>
}

/**
 * Creates a Collection.
 * @param collectionOpts
 */
export function collection<A, T extends Model<A, unknown>>(
  collectionOpts: CollectionOptions<A, T>,
): Collection<A, T> {
  const setOpts = {
    stripUndefined: true,
    parse: true as const,
  }
  collectionOpts = {
    getDataId: (attrs: any) =>
      attrs && attrs[collectionOpts.idAttribute || 'id'],
    getModelId: (existing: any) => existing[collectionOpts.idAttribute || 'id'],
    ...collectionOpts,
  }
  const collectionInstance = libxCollection<T>({
    ...(collectionOpts as any),
    create: (attributes: Partial<A>) => {
      const opts = {
        stripUndefined: true,
        parse: true as const,
      }
      const instance = collectionOpts.create(attributes)
      instance.set(attributes, opts)
      return instance
    },
    update: (existing, input) => existing.set(input, setOpts),
  }) as Collection<A, T>

  // This is used to make sure we don't emit double removals when resetting the internal collection
  // array in reset([])
  let silenceRemovals = false
  collectionInstance.reset = action((data?: Partial<A>[]) => {
    if (!data) {
      return collectionInstance
    }
    const getDataId = collectionOpts.getDataId!
    const getModelId = collectionOpts.getModelId!
    const dataMap = new Map(
      data.map((d) => [getDataId(d, collectionOpts), true]),
    )
    const itemsBeforeSet = collectionInstance.slice()
    const newItems = collectionInstance.set(data)
    // Remove elements that are no longer there.
    // Go through the proper method to make sure the internal ID cache is
    // updated.
    itemsBeforeSet.forEach((model) => {
      const isInData = dataMap.has(getModelId(model, collectionOpts))
      if (!isInData) {
        collectionInstance.remove(model)
      }
    })

    // Replace the internal array with the new items to get the proper ordering.
    silenceRemovals = true
    collectionInstance.items.replace(newItems)
    silenceRemovals = false
    return collectionInstance
  })

  collectionInstance.removals$ = new Observable<IArrayUpdate | IArraySplice>(
    (observer) => {
      const dispose = observe(
        collectionInstance.items,
        observer.next.bind(observer),
      )
      return () => dispose()
    },
  ).pipe(
    concatMap((change) =>
      from(
        (change.type === 'splice' && !silenceRemovals
          ? change.removed
          : []) as T[],
      ),
    ),
    share(),
  )

  return collectionInstance
}
